import React, { useState, useCallback, useMemo } from 'react'
import {
    Button,
    Form,
    Input,
    InputNumber,
    Popover,
    message,
    Upload,
    Tooltip,
    Modal,
    Table,
    Spin
} from 'antd'
import { ToolOutlined, UploadOutlined } from '@ant-design/icons'
import axios from '../../util/Api'
import moment from 'moment'

// Helper function to check if user has 'c' or 'u' permission for a resource
const hasPermission = (roles, resource, permissionsNeeded) => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
        return false
    }

    const access = roles.map((item) => item.access || [])
    const rolesPermissions = access.flat(1).filter((item) => {
        return item.name === resource
    })

    if (rolesPermissions.length === 0) {
        return false
    }

    const permissions = rolesPermissions.map(item => item.permissions || []).flat(1)

    // Check if user has any of the required permissions
    const requiredPermissions = Array.isArray(permissionsNeeded) ? permissionsNeeded : [permissionsNeeded]
    return requiredPermissions.some(perm => permissions.indexOf(perm) > -1)
}

function RectifyCell({ record, field, value: propValue, onSuccess, roles, convertMinToMs = false }) {
    const [visible, setVisible] = useState(false)
    const [editValue, setEditValue] = useState(null)
    const [remarks, setRemarks] = useState('')
    const [fileList, setFileList] = useState([])
    const [logModalVisible, setLogModalVisible] = useState(false)
    const [logData, setLogData] = useState([])
    const [logLoading, setLogLoading] = useState(false)

    // Helper function to convert minutes to milliseconds
    const minutesToMs = useCallback((minutes) => {
        return minutes * 60 * 1000
    }, [])

    // Helper function to convert milliseconds to minutes
    const msToMinutes = useCallback((ms) => {
        return ms / (60 * 1000)
    }, [])

    // Helper function to convert kilometers to meters
    const kmToMeters = useCallback((km) => {
        return km * 1000
    }, [])

    // Helper function to convert meters to kilometers
    const metersToKm = useCallback((meters) => {
        return meters / 1000
    }, [])

    const handleVisibleChange = useCallback((isVisible) => {
        console.log(record, field)
        if (isVisible) {
            // For fields that need conversion, propValue is in minutes, but record[field] might be in ms
            // We want to display in minutes, so use propValue directly
            setVisible(true)
            setEditValue(propValue || 0)
            setRemarks('')
            setFileList([])
        } else {
            setVisible(false)
            setEditValue(null)
            setRemarks('')
            setFileList([])
        }
    }, [record, field, propValue])

    const handleValueChange = useCallback((val) => {
        setEditValue(val)
    }, [])

    const handleRemarksChange = useCallback((e) => {
        setRemarks(e.target.value)
    }, [])

    const handleFileChange = useCallback((info) => {
        let newFileList = [...info.fileList]
        // Limit to 5 files
        newFileList = newFileList.slice(-5)
        setFileList(newFileList)
    }, [])

    const beforeUpload = useCallback((file) => {
        const isImage = file.type.startsWith('image/')
        if (!isImage) {
            message.error('You can only upload image files!')
            return false
        }
        const isLt5M = file.size / 1024 / 1024 < 5
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!')
            return false
        }
        return false // Prevent auto upload
    }, [])

    const handleSave = useCallback(async () => {
        const value = editValue !== null ? editValue : (propValue || 0)
        const originalValue = propValue || 0

        // Validate that value is not same as original
        if (Number(value) === Number(originalValue)) {
            message.error('New value must be different from the current value')
            return
        }

        if (value === null || value === undefined || value === '') {
            message.error('Please enter a valid value')
            return
        }

        if (!remarks || remarks.trim() === '') {
            message.error('Please enter a reason for this rectification')
            return
        }

        try {
            const recordIdForApi = record.daily_fuel_data_id || record._id

            if (!recordIdForApi) {
                message.error('Record ID is missing')
                return
            }

            // Create FormData for file upload
            const formData = new FormData()

            // Append all other data fields - these will be received by @Body() in NestJS
            // Note: FormData sends everything as strings, but backend validation expects numbers
            formData.append('ref_id', recordIdForApi)

            // Ensure date_timestamp is sent as a valid numeric string
            if (record.date_timestamp !== null && record.date_timestamp !== undefined) {
                let timestamp
                if (typeof record.date_timestamp === 'number') {
                    timestamp = record.date_timestamp
                } else if (record.date_timestamp instanceof Date) {
                    timestamp = record.date_timestamp.getTime()
                } else {
                    // Try to parse as date string and convert to timestamp
                    timestamp = new Date(record.date_timestamp).getTime()
                }
                // Ensure it's a valid number
                if (!isNaN(timestamp) && isFinite(timestamp)) {
                    formData.append('date_timestamp', timestamp.toString())
                }
            }

            if (record.device_id) {
                formData.append('device_id', record.device_id.toString())
            }

            if (record.asset && record.asset._id) {
                formData.append('asset', record.asset._id)
            }

            formData.append('field_name', field)

            // Ensure corrected_value is sent as a valid numeric string (not NaN or Infinity)
            // Convert based on field type
            let numValue = Number(value)
            if (convertMinToMs) {
                // Convert minutes to milliseconds for ideal_time and moving_time
                numValue = minutesToMs(numValue)
            } else if (field === 'distance_travelled') {
                // Convert kilometers to meters for distance_travelled
                numValue = kmToMeters(numValue)
            }
            if (isNaN(numValue) || !isFinite(numValue)) {
                message.error('Please enter a valid numeric value')
                return
            }
            formData.append('corrected_value', numValue.toString())
            formData.append('remark', remarks.trim())

            // Append files - these will be received by @UploadedFiles() in NestJS
            // Using 'files' as the field name (matches @UploadedFiles() parameter)
            fileList.forEach((file) => {
                const fileObj = file.originFileObj || file
                if (fileObj instanceof File) {
                    formData.append('files', fileObj)
                }
            })

            // Log FormData contents for debugging
            console.log('FormData entries:')
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ', pair[1])
            }

            // console.log('payload summary', {
            //     ref_id: recordIdForApi,
            //     date_timestamp: record.date_timestamp,
            //     device_id: record.device_id,
            //     asset: record.asset && record.asset._id,
            //     field_name: field,
            //     corrected_value: numValue,
            //     corrected_value_minutes: convertMinToMs ? value : null,
            //     remark: remarks.trim(),
            //     files_count: fileList.length
            // })

            // For FormData, use the codebase pattern
            // Note: When using FormData, browser should set Content-Type with boundary automatically
            await axios.post('/daily-fuel-data-rectification-log', formData, {
                config: {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            })

            if (onSuccess) {
                // Pass the value in the unit used for display (minutes if convertMinToMs, otherwise original unit)
                onSuccess(record, field, Number(value))
            }

            message.success('Value rectified successfully')
            setVisible(false)
            setEditValue(null)
            setRemarks('')
            setFileList([])
        } catch (error) {
            console.error('Error rectifying value:', error)
            console.error('Error response:', error.response)
            // console.error('Error response data:', error.response?.data)
            // const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to rectify value. Please try again.'
            // message.error(errorMessage)
        }
    }, [editValue, propValue, remarks, record, field, onSuccess, fileList, convertMinToMs, minutesToMs, kmToMeters])

    const handleCancel = useCallback(() => {
        setVisible(false)
        setEditValue(null)
        setRemarks('')
        setFileList([])
    }, [])

    // Check for original value
    // Convert based on field type: ms to minutes for ideal_time/moving_time, meters to km for distance_travelled
    const originalValue = useMemo(() => {
        const originalFieldName = `original_${field}`
        let value = record[originalFieldName]
        if (value !== null && value !== undefined) {
            if (convertMinToMs) {
                // Convert milliseconds to minutes for ideal_time and moving_time
                value = msToMinutes(value)
            } else if (field === 'distance_travelled') {
                // Convert meters to kilometers for distance_travelled
                value = metersToKm(value)
            }
        }
        return value
    }, [record, field, convertMinToMs, msToMinutes, metersToKm])

    // Handle click on current value to show rectification logs
    const handleCurrentValueClick = useCallback(async () => {
        const recordIdForApi = record.daily_fuel_data_id || record._id
        if (!recordIdForApi) {
            message.error('Record ID is missing')
            return
        }

        setLogModalVisible(true)
        setLogLoading(true)
        try {
            const response = await axios.get(`/daily-fuel-data-rectification-log/get-by-ref-id-and-field-name/${recordIdForApi}/${field}`)
            // console.log('Response:', response)
            setLogData(Array.isArray(response.data) ? response.data : [response.data])
        } catch (error) {
            console.error('Error fetching rectification logs:', error)
            message.error('Failed to fetch rectification logs')
            setLogData([])
        } finally {
            setLogLoading(false)
        }
    }, [record, field])

    const handleLogModalCancel = useCallback(() => {
        setLogModalVisible(false)
        setLogData([])
    }, [])

    // Check if field needs conversion
    const shouldConvertToMinutes = field === 'ideal_time' || field === 'moving_time'
    const shouldConvertToKm = field === 'distance_travelled'

    // Define columns for rectification log table
    const logColumns = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => date ? moment(date).format('DD-MM-YYYY HH:mm') : 'N/A'
        },
        {
            title: 'Old Value',
            dataIndex: 'old_value',
            key: 'old_value',
            width: 120,
            render: (value) => {
                if (value === null || value === undefined) return 'N/A'
                // Convert based on field type
                let displayValue = value
                if (shouldConvertToMinutes) {
                    // Convert from ms to minutes for ideal_time and moving_time
                    displayValue = msToMinutes(value)
                } else if (shouldConvertToKm) {
                    // Convert from meters to kilometers for distance_travelled
                    displayValue = metersToKm(value)
                }
                return Number(displayValue).toFixed(2)
            }
        },
        {
            title: 'Corrected Value',
            dataIndex: 'corrected_value',
            key: 'corrected_value',
            width: 120,
            render: (value) => {
                if (value === null || value === undefined) return 'N/A'
                // Convert based on field type
                let displayValue = value
                if (shouldConvertToMinutes) {
                    // Convert from ms to minutes for ideal_time and moving_time
                    displayValue = msToMinutes(value)
                } else if (shouldConvertToKm) {
                    // Convert from meters to kilometers for distance_travelled
                    displayValue = metersToKm(value)
                }
                return Number(displayValue).toFixed(2)
            }
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            width: 200,
            render: (text) => text || 'N/A'
        },
        {
            title: 'Rectified By',
            dataIndex: 'created_by',
            key: 'created_by',
            width: 150,
            render: (user) => {
                if (user && typeof user === 'object') {
                    return user.name || user.email || 'N/A'
                }
                return user || 'N/A'
            }
        },
        {
            title: 'Photos',
            dataIndex: 'files',
            key: 'photos',
            width: 200,
            render: (files, record) => {
                // Handle different possible structures: files, photos, images, etc.
                const imageFiles = files || record.photos || record.images || []

                if (!Array.isArray(imageFiles) || imageFiles.length === 0) {
                    return 'N/A'
                }

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {imageFiles.map((file, index) => {
                            // Handle different file structures
                            const fileName = file.name || file.filename || (file.file && file.file.name) || `Image ${index + 1}`
                            const fileUrl = file.url || file.path || (file.file && (file.file.path || file.file.url)) || '#'

                            return (
                                <a
                                    key={index}
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#1890ff',
                                        textDecoration: 'underline',
                                        display: 'block',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {fileName}
                                </a>
                            )
                        })}
                    </div>
                )
            }
        }
    ]

    const currentValue = editValue !== null ? editValue : (propValue || 0)
    const fieldTitle = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

    // Get asset code and formatted date for modal title
    const assetCode = record.asset && record.asset.assetCode ? record.asset.assetCode : 'N/A'
    const recordDate = record.date_timestamp
        ? moment(record.date_timestamp).format('DD-MM-YYYY')
        : 'N/A'
    const modalTitle = `Rectification History - ${fieldTitle} (Asset: ${assetCode}, Date: ${recordDate})`

    const editFormContent = (
        <div style={{ width: 350 }}>
            <Form layout="vertical">
                <Form.Item label="Value">
                    <InputNumber
                        style={{ width: '100%' }}
                        value={currentValue}
                        onChange={handleValueChange}
                        placeholder="Enter value"
                        min={0}
                        step={0.01}
                        precision={2}
                    />
                </Form.Item>
                <Form.Item label="Remarks (Reason for Rectification)">
                    <Input.TextArea
                        value={remarks}
                        onChange={handleRemarksChange}
                        placeholder="Enter reason for this Rectification"
                        rows={3}
                    />
                </Form.Item>
                <Form.Item label="Upload Photos (Optional)">
                    <Upload
                        fileList={fileList}
                        onChange={handleFileChange}
                        beforeUpload={beforeUpload}
                        multiple
                        accept="image/*"
                        listType="picture-card"
                    >
                        {fileList.length >= 5 ? null : (
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        )}
                    </Upload>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        Maximum 5 images, each under 5MB
                    </div>
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex' }}>
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleSave}
                            style={{ marginRight: 8 }}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </div>
    )

    // Check if user has 'c' or 'u' permission for DailyFuelDataRectification
    const canRectify = useMemo(() => {
        return hasPermission(roles, 'DailyFuelDataRectification', ['c', 'u'])
    }, [roles])

    // Render value display
    const renderValueDisplay = () => {
        if (originalValue !== null && originalValue !== undefined) {
            // Show both original (strikethrough) and current (clickable) values
            return (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ textDecoration: 'line-through', color: '#999' }}>
                        {Number(originalValue).toFixed(2)}
                    </span>
                    <span
                        onClick={handleCurrentValueClick}
                        style={{
                            cursor: 'pointer',
                            color: '#1890ff',
                            textDecoration: 'underline',
                            fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = '#40a9ff'
                            e.target.style.textDecoration = 'underline'
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = '#1890ff'
                            e.target.style.textDecoration = 'underline'
                        }}
                        title="Click to view rectification history"
                    >
                        {propValue ? Number(propValue).toFixed(2) : '0.00'}
                    </span>
                </div>
            )
        } else {
            // Show only current value (not clickable if no original value)
            return (
                <span style={{ flex: 1 }}>
                    {propValue ? Number(propValue).toFixed(2) : '0.00'}
                </span>
            )
        }
    }

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {renderValueDisplay()}
                {canRectify && (
                    <Popover
                        content={editFormContent}
                        title={`Rectify ${fieldTitle}`}
                        trigger="click"
                        visible={visible}
                        onVisibleChange={handleVisibleChange}
                        placement="left"
                    >
                        <Tooltip title={`Rectify ${fieldTitle}`}>
                            <span
                                style={{
                                    cursor: 'pointer',
                                    color: '#1890ff',
                                    fontSize: '16px',
                                    marginLeft: 8,
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                }}
                            >
                                <ToolOutlined />
                            </span>
                        </Tooltip>
                    </Popover>
                )}
            </div>
            <Modal
                title={modalTitle}
                visible={logModalVisible}
                onCancel={handleLogModalCancel}
                footer={null}
                width={800}
                destroyOnClose={true}
            >
                <Spin spinning={logLoading}>
                    <Table
                        columns={logColumns}
                        dataSource={logData}
                        rowKey={(record, index) => record._id || record.id || index}
                        pagination={{ pageSize: 10 }}
                        bordered
                        size="small"
                        scroll={{ x: 900 }}
                    />
                </Spin>
            </Modal>
        </>
    )
}

export default RectifyCell

